import React, { FC } from 'react';
import Box from '@mui/material/Box';
import {
  DataGridPremium,
  GridCellParams,
  GridColDef,
  GridRowParams,
} from '@mui/x-data-grid-premium';
import { FinancialFrameGlobalTableProps } from './FinancialFrameTableProps';
import {
  findFrameGroupByField,
  NON_CLICKABLE_ROW,
  returnFinancialFramesColumnGroupingModel,
  returnFinancialFramesTableColumns,
  returnFinancialFramesTableSx,
} from './utils';
import useTranslation from '../../hooks/useTranslation';
import { FinancialFrame } from '../../store/financialFrames/stateType';

const ROW_TYPE_ORDER = { GLOBAL: 0, UNALLOCATED: 1 };

const FinancialFrameGlobalTable: FC<FinancialFrameGlobalTableProps> = ({
  financialFrames,
  selectedFYs,
  editAllowed,
  onRowClick,
  onValueCellClick,
}) => {
  const t = useTranslation();

  const rows = financialFrames
    .filter(
      (frame) => !selectedFYs.length || selectedFYs.includes(frame.fiscalYear),
    )
    .sort(
      (a, b) =>
        a.fiscalYear.localeCompare(b.fiscalYear) ||
        ROW_TYPE_ORDER[a.rowType] - ROW_TYPE_ORDER[b.rowType],
    );

  const columns = returnFinancialFramesTableColumns(t, []).map((column) =>
    column.field === 'countryName'
      ? {
          ...column,
          headerName: t('financialFrames.global'),
          sortable: false,
          valueGetter: (_value, row: FinancialFrame) =>
            row.rowType === 'UNALLOCATED'
              ? t('financialFrames.unallocatedRmpp')
              : t('financialFrames.globalRmpp'),
        }
      : { ...column, sortable: false, filterable: false },
  );

  const isClickable = (row: FinancialFrame) =>
    editAllowed && row.rowType === 'GLOBAL';

  const onCellClick = (params: GridCellParams<FinancialFrame>) => {
    if (!isClickable(params.row)) return;
    const group = findFrameGroupByField(params.field);
    if (group) {
      onValueCellClick(params.row, group.group);
    } else {
      onRowClick(params.row);
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      <DataGridPremium
        autoHeight
        disableRowSelectionOnClick
        disableColumnMenu
        hideFooter
        sx={returnFinancialFramesTableSx(editAllowed)}
        initialState={{
          pinnedColumns: { left: ['countryName', 'fiscalYear'] },
          columns: { columnVisibilityModel: { countryId: false } },
        }}
        columnGroupingModel={returnFinancialFramesColumnGroupingModel(t)}
        columns={columns as GridColDef[]}
        rows={rows}
        getRowId={(row: FinancialFrame) => `${row.rowType}-${row.fiscalYear}`}
        getRowClassName={(params: GridRowParams<FinancialFrame>) =>
          isClickable(params.row) ? '' : NON_CLICKABLE_ROW
        }
        onCellClick={onCellClick}
      />
    </Box>
  );
};

export default FinancialFrameGlobalTable;
